/**
 * MTD Review Card - "How Am I Doing This Month?"
 *
 * Provides at-a-glance month-to-date performance tracking with:
 * - Team view (aggregated totals) or individual rep view
 * - 5 key metrics: Revenue, Apps In, Calls, Funded, Approved
 * - Color-coded status badges
 * - Detailed, actionable guidance (targets + performance + quick wins)
 *
 * Created: November 8, 2025
 */

import React, { useMemo, useCallback } from 'react';
import '../index.css';

const MTDReviewCard = ({ data, goals, reps, month, year = new Date().getFullYear(), monthYear, selectedView, setSelectedView, developerMode }) => {
  // ✨ UPDATED (Nov 17, 2025): selectedView now lifted to Dashboard.js for sync with trending components
  // ✨ UPDATED (Jan 2, 2026): Added year and monthYear props for proper year filtering

  // Helper: Use monthYear for goals (e.g., "January 2026"), fallback to legacy format
  const goalMonth = monthYear || `${month} ${year}`;

  // Helper: Filter by month AND year (uses Date column)
  const filterByMonthYear = useCallback((dataArray) => {
    return dataArray?.filter(row =>
      row.Month === month && row.Date && new Date(row.Date).getFullYear() === year
    ) || [];
  }, [month, year]);

  // ✅ ACTUAL BUSINESS DAYS CALCULATION (Mon-Fri only, from Dashboard.js)
  const getBusinessDaysElapsed = () => {
    const today = new Date();
    const monthIndex = today.getMonth();

    let businessDays = 0;
    for (let day = 1; day <= today.getDate(); day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      // 0 = Sunday, 6 = Saturday
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  };

  const getBusinessDaysRemaining = () => {
    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth();
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();

    let businessDays = 0;
    for (let day = today.getDate() + 1; day <= lastDay; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  };

  const getBusinessDaysInMonth = () => {
    const today = new Date();
    const year = today.getFullYear();
    const monthIndex = today.getMonth();
    const lastDay = new Date(year, monthIndex + 1, 0).getDate();

    let businessDays = 0;
    for (let day = 1; day <= lastDay; day++) {
      const date = new Date(year, monthIndex, day);
      const dayOfWeek = date.getDay();
      if (dayOfWeek !== 0 && dayOfWeek !== 6) {
        businessDays++;
      }
    }
    return businessDays;
  };

  const businessDaysElapsed = getBusinessDaysElapsed();
  const businessDaysRemaining = getBusinessDaysRemaining();
  const businessDaysInMonth = getBusinessDaysInMonth();

  // Helper: Filter out $0 deals (matches Dashboard.js filterValidDeals)
  const filterValidDeals = (records) => {
    return records.filter(r => {
      const amount = parseFloat(r.Amount) || parseFloat(r.Revenue) || 0;
      return amount > 0;
    });
  };

  // Calculate metrics based on selected view (Team or Individual Rep)
  const viewMetrics = useMemo(() => {
    if (!data || !data.tabs || !goals) return null;

    if (selectedView === 'Team') {
      // TEAM VIEW: Aggregate all reps' MTD data
      const teamData = {
        current: { revenue: 0, apps: 0, calls: 0, funded: 0, approved: 0 },
        goals: { revenue: 0, apps: 0, calls: 0, funded: 0, approved: 0 }
      };

      reps.forEach(rep => {
        // ✅ FIX (Nov 8, 2025): Filter by Month, Year (from Date column), AND Rep
        const repApps = filterByMonthYear(data.tabs.Apps_In?.data).filter(r => r.Rep === rep);
        const repApproved = filterByMonthYear(data.tabs.Approved?.data).filter(r => r.Rep === rep);
        const repCalls = filterByMonthYear(data.tabs.Dials_TalkTime?.data).filter(r => r.Rep === rep);
        const repFunded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data).filter(r => r.Rep === rep));

        // ✅ FIX: Revenue comes from Revenue tab (matches Daily Breakdown logic)
        const repRevenue = filterValidDeals(filterByMonthYear(data.tabs.Revenue?.data).filter(r => r.Rep === rep));
        const totalRevenue = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

        teamData.current.revenue += totalRevenue;
        teamData.current.apps += repApps.length;
        teamData.current.calls += repCalls.length;
        teamData.current.funded += repFunded.length;
        teamData.current.approved += repApproved.length;

        // ✅ FIX: Filter goals by Month Year format AND Rep
        const repGoal = goals.find(g => g.Month === goalMonth && g.Rep === rep);
        if (repGoal) {
          teamData.goals.revenue += parseFloat(repGoal.Revenue_Goal) || 0;
          teamData.goals.apps += parseFloat(repGoal.Apps_Goal) || 0;
          // ✅ FIX (Nov 8, 2025): Activity_Call_Goal is DAILY, multiply by business days for monthly goal
          teamData.goals.calls += (parseFloat(repGoal.Activity_Call_Goal) || 0) * businessDaysInMonth;
          teamData.goals.funded += parseFloat(repGoal.Funded_Units_Goal) || 0;
          teamData.goals.approved += parseFloat(repGoal.Approved_Goal) || 0;
        }
      });

      return teamData;
    } else {
      // INDIVIDUAL REP VIEW
      // ✅ FIX: Filter by Month, Year (from Date column), AND Rep
      const repApps = filterByMonthYear(data.tabs.Apps_In?.data).filter(r => r.Rep === selectedView);
      const repApproved = filterByMonthYear(data.tabs.Approved?.data).filter(r => r.Rep === selectedView);
      const repCalls = filterByMonthYear(data.tabs.Dials_TalkTime?.data).filter(r => r.Rep === selectedView);
      const repFunded = filterValidDeals(filterByMonthYear(data.tabs.Funded?.data).filter(r => r.Rep === selectedView));

      // ✅ FIX: Revenue comes from Revenue tab (matches Daily Breakdown logic)
      const repRevenue = filterValidDeals(filterByMonthYear(data.tabs.Revenue?.data).filter(r => r.Rep === selectedView));
      const totalRevenue = repRevenue.reduce((sum, r) => sum + (parseFloat(r.Revenue) || 0), 0);

      // ✅ FIX: Filter goals by Month Year format AND Rep
      const repGoal = goals.find(g => g.Month === goalMonth && g.Rep === selectedView);

      return {
        current: {
          revenue: totalRevenue,
          apps: repApps.length,
          calls: repCalls.length,
          funded: repFunded.length,
          approved: repApproved.length
        },
        goals: {
          revenue: parseFloat(repGoal?.Revenue_Goal) || 0,
          apps: parseFloat(repGoal?.Apps_Goal) || 0,
          // ✅ FIX (Nov 8, 2025): Activity_Call_Goal is DAILY, multiply by business days for monthly goal
          calls: (parseFloat(repGoal?.Activity_Call_Goal) || 0) * businessDaysInMonth,
          funded: parseFloat(repGoal?.Funded_Units_Goal) || 0,
          approved: parseFloat(repGoal?.Approved_Goal) || 0
        }
      };
    }
  }, [data, goals, reps, selectedView, month, businessDaysInMonth, filterByMonthYear, goalMonth]);

  // ✅ PACE-BASED STATUS: Compare actual vs expected based on business days worked
  const getStatus = (current, goal, businessDaysElapsed, businessDaysInMonth) => {
    if (!goal) return { label: 'NO GOAL', icon: '—', color: '#94a3b8' };

    const expected = (goal / businessDaysInMonth) * businessDaysElapsed;
    const pace = expected > 0 ? (current / expected) * 100 : 0;

    if (pace >= 80) return { label: 'ON TRACK', icon: '✅', color: '#10b981' };
    if (pace >= 60) return { label: 'BEHIND', icon: '⚠️', color: '#f59e0b' };
    return { label: 'CRITICAL', icon: '🚨', color: '#ef4444' };
  };

  const getBadgeStatus = (current, goal, businessDaysElapsed, businessDaysInMonth) => {
    if (!goal) return { icon: '—', color: '#94a3b8' };

    const expected = (goal / businessDaysInMonth) * businessDaysElapsed;
    const pace = expected > 0 ? (current / expected) * 100 : 0;

    if (pace >= 80) return { icon: '✅', color: '#10b981' };
    if (pace >= 60) return { icon: '⚠️', color: '#f59e0b' };
    return { icon: '🚨', color: '#ef4444' };
  };

  // Helper: Calculate expected value by now
  const getExpected = (goal, businessDaysElapsed, businessDaysInMonth) => {
    if (!goal || !businessDaysInMonth) return 0;
    return (goal / businessDaysInMonth) * businessDaysElapsed;
  };

  // Format currency
  const formatCurrency = (value) => new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(value);

  // Format large numbers
  const formatNumber = (value) => new Intl.NumberFormat('en-US').format(value);

  if (!viewMetrics) {
    return <div className="mtd-review-card loading">Loading MTD data...</div>;
  }

  const { current, goals: goalValues } = viewMetrics;

  // ✅ Calculate expected values based on actual business days worked
  const revenueExpected = getExpected(goalValues.revenue, businessDaysElapsed, businessDaysInMonth);
  const appsExpected = getExpected(goalValues.apps, businessDaysElapsed, businessDaysInMonth);
  const callsExpected = getExpected(goalValues.calls, businessDaysElapsed, businessDaysInMonth);
  const fundedExpected = getExpected(goalValues.funded, businessDaysElapsed, businessDaysInMonth);
  const approvedExpected = getExpected(goalValues.approved, businessDaysElapsed, businessDaysInMonth);

  // ✅ Overall status based on revenue (primary metric)
  const overallStatus = getStatus(current.revenue, goalValues.revenue, businessDaysElapsed, businessDaysInMonth);

  // ✅ Calculate what's needed (guidance) - per day, not approximations
  const revenueNeeded = Math.max(0, goalValues.revenue - current.revenue);
  const revenuePerDay = businessDaysRemaining > 0 ? Math.ceil(revenueNeeded / businessDaysRemaining) : 0;

  const callsNeeded = Math.max(0, goalValues.calls - current.calls);
  const callsPerDay = businessDaysRemaining > 0 ? Math.ceil(callsNeeded / businessDaysRemaining) : 0;

  const appsNeeded = Math.max(0, goalValues.apps - current.apps);
  const appsPerDay = businessDaysRemaining > 0 ? Math.ceil(appsNeeded / businessDaysRemaining) : 0;

  const fundedNeeded = Math.max(0, goalValues.funded - current.funded);
  const fundedPerDay = businessDaysRemaining > 0 ? Math.ceil(fundedNeeded / businessDaysRemaining) : 0;

  const displayName = selectedView === 'Team' ? 'the Team' : selectedView.split(' ')[0];

  return (
    <div className="mtd-review-card" style={{ position: 'relative' }}>
      {/* Developer Mode: Section Label */}
      {developerMode && (
        <div className="dev-section-label">S-1</div>
      )}
      {/* Header with dropdown */}
      <div className="mtd-header">
        <h2>🎯 How Am I Doing This Month? ({month})</h2>
        <select
          value={selectedView}
          onChange={(e) => setSelectedView(e.target.value)}
          className="view-selector"
        >
          <option value="Team">Team</option>
          {reps.map(rep => (
            <option key={rep} value={rep}>{rep.split(' ')[0]}</option>
          ))}
        </select>
      </div>

      {/* Status line */}
      <div className="status-line" style={{ color: overallStatus.color }}>
        {overallStatus.icon} Status: <strong>{overallStatus.label}</strong> | {businessDaysRemaining} business days remaining
      </div>

      {/* Metrics grid */}
      <div className="metrics-grid">
        {/* Revenue */}
        <div className="metric-card">
          <div className="metric-label">Revenue</div>
          <div className="metric-value">{formatCurrency(current.revenue)}</div>
          <div className="metric-expected" style={{fontSize: '12px', color: '#64748b', marginTop: '4px'}}>
            Expected: {formatCurrency(revenueExpected)} (by day {businessDaysElapsed})
          </div>
          <div className="metric-goal">Goal: {formatCurrency(goalValues.revenue)}</div>
          <div className="metric-progress" style={{ color: getBadgeStatus(current.revenue, goalValues.revenue, businessDaysElapsed, businessDaysInMonth).color }}>
            {getBadgeStatus(current.revenue, goalValues.revenue, businessDaysElapsed, businessDaysInMonth).icon} {current.revenue >= revenueExpected ? 'Ahead' : 'Behind'} {formatCurrency(Math.abs(current.revenue - revenueExpected))}
          </div>
        </div>

        {/* Apps In */}
        <div className="metric-card">
          <div className="metric-label">Apps In</div>
          <div className="metric-value">{formatNumber(current.apps)}</div>
          <div className="metric-expected" style={{fontSize: '12px', color: '#64748b', marginTop: '4px'}}>
            Expected: {Math.round(appsExpected)} (by day {businessDaysElapsed})
          </div>
          <div className="metric-goal">Goal: {formatNumber(goalValues.apps)}</div>
          <div className="metric-progress" style={{ color: getBadgeStatus(current.apps, goalValues.apps, businessDaysElapsed, businessDaysInMonth).color }}>
            {getBadgeStatus(current.apps, goalValues.apps, businessDaysElapsed, businessDaysInMonth).icon} {current.apps >= appsExpected ? 'Ahead' : 'Behind'} {Math.abs(current.apps - Math.round(appsExpected))}
          </div>
        </div>

        {/* Calls */}
        <div className="metric-card">
          <div className="metric-label">Calls</div>
          <div className="metric-value">{formatNumber(current.calls)}</div>
          <div className="metric-expected" style={{fontSize: '12px', color: '#64748b', marginTop: '4px'}}>
            Expected: {Math.round(callsExpected)} (by day {businessDaysElapsed})
          </div>
          <div className="metric-goal">Goal: {formatNumber(goalValues.calls)}</div>
          <div className="metric-progress" style={{ color: getBadgeStatus(current.calls, goalValues.calls, businessDaysElapsed, businessDaysInMonth).color }}>
            {getBadgeStatus(current.calls, goalValues.calls, businessDaysElapsed, businessDaysInMonth).icon} {current.calls >= callsExpected ? 'Ahead' : 'Behind'} {Math.abs(current.calls - Math.round(callsExpected))}
          </div>
        </div>

        {/* Funded */}
        <div className="metric-card">
          <div className="metric-label">Funded</div>
          <div className="metric-value">{formatNumber(current.funded)}</div>
          <div className="metric-expected" style={{fontSize: '12px', color: '#64748b', marginTop: '4px'}}>
            Expected: {Math.round(fundedExpected)} (by day {businessDaysElapsed})
          </div>
          <div className="metric-goal">Goal: {formatNumber(goalValues.funded)}</div>
          <div className="metric-progress" style={{ color: getBadgeStatus(current.funded, goalValues.funded, businessDaysElapsed, businessDaysInMonth).color }}>
            {getBadgeStatus(current.funded, goalValues.funded, businessDaysElapsed, businessDaysInMonth).icon} {current.funded >= fundedExpected ? 'Ahead' : 'Behind'} {Math.abs(current.funded - Math.round(fundedExpected))}
          </div>
        </div>

        {/* Approved */}
        <div className="metric-card">
          <div className="metric-label">Approved</div>
          <div className="metric-value">{formatNumber(current.approved)}</div>
          <div className="metric-expected" style={{fontSize: '12px', color: '#64748b', marginTop: '4px'}}>
            Expected: {Math.round(approvedExpected)} (by day {businessDaysElapsed})
          </div>
          <div className="metric-goal">Goal: {formatNumber(goalValues.approved)}</div>
          <div className="metric-progress" style={{ color: getBadgeStatus(current.approved, goalValues.approved, businessDaysElapsed, businessDaysInMonth).color }}>
            {getBadgeStatus(current.approved, goalValues.approved, businessDaysElapsed, businessDaysInMonth).icon} {current.approved >= approvedExpected ? 'Ahead' : 'Behind'} {Math.abs(current.approved - Math.round(approvedExpected))}
          </div>
        </div>
      </div>

      {/* Guidance section */}
      <div className="guidance-section">
        <h3>📊 What's Needed to Hit Goal:</h3>

        {/* Revenue guidance */}
        {revenueNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>💰</span>
            <div><strong>Revenue:</strong> Need {formatCurrency(revenueNeeded)} more → {formatCurrency(revenuePerDay)}/day for next {businessDaysRemaining} business days
            {selectedView === 'Team' && ` (${formatCurrency(Math.ceil(revenuePerDay / reps.length))} per rep/day)`}</div>
          </div>
        )}
        {revenueNeeded <= 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>💰</span>
            <div><strong>Revenue:</strong>
            <span style={{ color: '#10b981', marginLeft: '8px' }}>
              ✅ Revenue goal achieved! Outstanding work!
            </span></div>
          </div>
        )}

        {/* Apps guidance */}
        {appsNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>📋</span>
            <div><strong>Apps:</strong> Need {appsNeeded} more → {appsPerDay}/day for next {businessDaysRemaining} business days
            {selectedView === 'Team' && ` (${Math.ceil(appsPerDay / reps.length)} per rep/day)`}</div>
          </div>
        )}
        {appsNeeded <= 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>📋</span>
            <div><strong>Apps:</strong>
            <span style={{ color: '#10b981', marginLeft: '8px' }}>
              ✅ Apps goal achieved! Excellent work!
            </span></div>
          </div>
        )}

        {/* Calls guidance */}
        {callsNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>📞</span>
            <div><strong>Calls:</strong> Need {callsNeeded} more → {callsPerDay}/day for next {businessDaysRemaining} business days
            {selectedView === 'Team' && ` (${Math.ceil(callsPerDay / reps.length)} per rep/day)`}</div>
          </div>
        )}
        {callsNeeded <= 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>📞</span>
            <div><strong>Calls:</strong>
            <span style={{ color: '#10b981', marginLeft: '8px' }}>
              ✅ Call goal achieved! Keep up the great work!
            </span></div>
          </div>
        )}

        {/* Funded guidance */}
        {fundedNeeded > 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>✅</span>
            <div><strong>Funded:</strong> Need {fundedNeeded} more deal{fundedNeeded === 1 ? '' : 's'} → {fundedPerDay}/day for next {businessDaysRemaining} business days
            {selectedView === 'Team' && ` (${Math.ceil(fundedPerDay / reps.length)} per rep/day)`}</div>
          </div>
        )}
        {fundedNeeded <= 0 && (
          <div className="guidance-item" style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
            <span style={{ fontSize: '20px', marginTop: '2px' }}>✅</span>
            <div><strong>Funded:</strong>
            <span style={{ color: '#10b981', marginLeft: '8px' }}>
              Goal achieved! Outstanding performance!
            </span></div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MTDReviewCard;
